import { resolveRenewalEndpoints } from './activationConstants';
import { ActivationCertificateVerifier } from './ActivationCertificateVerifier';
import { ClockTamperGuard } from './ClockTamperGuard';
import type { LicenceRuntime } from './LicenceRuntime';
import { SecureLicenceStore } from './SecureLicenceStore';
import { LicenceStatusService } from './LicenceStatusService';
import { logger } from '@infra/telemetry/Logger';
import type { AppContext } from '@application/context/AppContext';
import { NetworkStatusService } from '@infra/network/NetworkStatusService';
import {
  notifyLicenceUpdated,
  syncLicenceEntitlements,
} from '@application/licence/LicenceEntitlementService';
import {
  outcomeFromServerCode,
  parseRenewalState,
  type RenewalOutcome,
  type RenewalState,
} from './LicenceRenewalStatus';

/** Renouvellement calendaire (prolongation) — max 1× / jour. */
const RENEWAL_MIN_INTERVAL_MS = 24 * 3600 * 1000;
/** Proche de l'échéance (ou échu) : on réessaie plus souvent pour récupérer vite un paiement validé. */
const RENEWAL_URGENT_INTERVAL_MS = 6 * 3600 * 1000;
const RENEWAL_URGENT_DAYS = 7;
/** Contrôle périodique tant que l'application reste ouverte (jours de fonctionnement continu). */
export const RENEWAL_CHECK_INTERVAL_MS = 6 * 3600 * 1000;
/** Synchronisation des modules signés — max 1× / jour (local et distant). */
export const ENTITLEMENT_SYNC_MIN_INTERVAL_MS = 24 * 3600 * 1000;

export type EntitlementSyncOptions = {
  /** Contourne la limite quotidienne (action manuelle admin). */
  force?: boolean;
};

/** Renouvellement et synchronisation des droits licence. */
export class LicenceRenewalService {
  private static scheduled = false;
  private static onlineWatcherAttached = false;
  private static syncInFlight: Promise<boolean> | null = null;
  private static renewInFlight: Promise<void> | null = null;
  private static timer: ReturnType<typeof setInterval> | null = null;

  /**
   * Renouvellement automatique AVANT l'échéance : essai au démarrage (5 s), puis toutes les 6 h tant
   * que l'application reste ouverte, et à chaque retour d'Internet. Hors ligne, rien n'est perdu :
   * l'essai reprend dès que la connexion revient (tant que le certificat est dans le délai de grâce
   * du serveur, 30 jours après l'échéance).
   */
  static schedule(runtime: LicenceRuntime, ctx?: AppContext): void {
    if (LicenceRenewalService.scheduled) return;
    LicenceRenewalService.scheduled = true;
    setTimeout(() => {
      void LicenceRenewalService.tryRenew(runtime, ctx);
    }, 5_000);
    LicenceRenewalService.timer = setInterval(() => {
      void LicenceRenewalService.tryRenew(runtime, ctx);
    }, RENEWAL_CHECK_INTERVAL_MS);
    // subscribe() rappelle l'écouteur tout de suite : on ignore cet appel initial (l'essai de démarrage a son délai).
    let ready = false;
    NetworkStatusService.subscribe((online) => {
      if (ready && online) void LicenceRenewalService.tryRenew(runtime, ctx);
    });
    ready = true;
  }

  /** Dernier résultat connu du renouvellement automatique de l'entreprise (pour l'écran Licence). */
  static async getRenewalState(tenantId: string): Promise<RenewalState | null> {
    return parseRenewalState(await SecureLicenceStore.getRenewalState(tenantId));
  }

  private static async recordRenewalState(
    tenantId: string,
    outcome: RenewalOutcome,
    code?: string,
  ): Promise<void> {
    const state: RenewalState = {
      lastAttemptAt: new Date().toISOString(),
      outcome,
      ...(code ? { code } : {}),
    };
    await SecureLicenceStore.setRenewalState(tenantId, JSON.stringify(state));
  }

  static __resetForTests(): void {
    if (LicenceRenewalService.timer) clearInterval(LicenceRenewalService.timer);
    LicenceRenewalService.timer = null;
    LicenceRenewalService.scheduled = false;
    LicenceRenewalService.onlineWatcherAttached = false;
    LicenceRenewalService.syncInFlight = null;
    LicenceRenewalService.renewInFlight = null;
  }

  /**
   * Tente une sync en arrière-plan au démarrage, puis à chaque retour réseau.
   * Non bloquant : le certificat local reste la source de vérité hors connexion.
   */
  static watchEntitlementSync(runtime: LicenceRuntime, ctx: AppContext): void {
    void LicenceRenewalService.syncEntitlementsFromServer(runtime, ctx);

    if (LicenceRenewalService.onlineWatcherAttached) return;
    LicenceRenewalService.onlineWatcherAttached = true;

    NetworkStatusService.subscribe((online) => {
      if (!online) return;
      void LicenceRenewalService.syncEntitlementsFromServer(runtime, ctx);
    });
  }

  /**
   * Récupère un certificat à jour (modules du plan) via /v1/activations/renew.
   * Respecte la limite quotidienne sauf si `force: true`.
   */
  static syncEntitlementsFromServer(
    runtime: LicenceRuntime,
    ctx?: AppContext,
    options: EntitlementSyncOptions = {},
  ): Promise<boolean> {
    if (LicenceRenewalService.syncInFlight) {
      return LicenceRenewalService.syncInFlight;
    }

    LicenceRenewalService.syncInFlight = LicenceRenewalService.runEntitlementSync(
      runtime,
      ctx,
      options,
    ).finally(() => {
      LicenceRenewalService.syncInFlight = null;
    });

    return LicenceRenewalService.syncInFlight;
  }

  private static async runEntitlementSync(
    runtime: LicenceRuntime,
    ctx?: AppContext,
    options: EntitlementSyncOptions = {},
  ): Promise<boolean> {
    const state = runtime.getState();
    if (!state.valid || !state.claims) return false;

    if (!NetworkStatusService.isBrowserOnline()) {
      logger.info({ event: 'entitlement_sync_skipped_offline' });
      return false;
    }

    const activeTenantId = state.claims.tenant_id;
    if (!options.force) {
      const lastVerified = await SecureLicenceStore.getLastVerifiedAt(activeTenantId);
      if (lastVerified) {
        const elapsed = Date.now() - new Date(lastVerified).getTime();
        if (elapsed < ENTITLEMENT_SYNC_MIN_INTERVAL_MS) {
          logger.info({ event: 'entitlement_sync_skipped_recent', elapsed_ms: elapsed });
          return false;
        }
      }
    }

    const previousFeatures = [...(state.claims.features ?? [])].sort().join(',');
    const ok = await LicenceRenewalService.exchangeCertificate(runtime, ctx);
    if (!ok) return false;

    await SecureLicenceStore.setLastVerifiedAt(activeTenantId, new Date().toISOString());
    const nextFeatures = [...(runtime.getState().claims?.features ?? [])].sort().join(',');
    if (previousFeatures !== nextFeatures) {
      logger.info({
        event: 'entitlements_changed',
        before: previousFeatures,
        after: nextFeatures,
      });
    }
    return true;
  }

  /**
   * Renouvellement calendaire de CHAQUE certificat du portefeuille (P0-3) : une entreprise
   * non active n'est ni oubliée ni renouvelée avec le certificat d'une autre.
   */
  static tryRenew(runtime: LicenceRuntime, ctx?: AppContext): Promise<void> {
    if (LicenceRenewalService.renewInFlight) return LicenceRenewalService.renewInFlight;
    LicenceRenewalService.renewInFlight = LicenceRenewalService.runRenew(runtime, ctx).finally(() => {
      LicenceRenewalService.renewInFlight = null;
    });
    return LicenceRenewalService.renewInFlight;
  }

  private static async runRenew(runtime: LicenceRuntime, ctx?: AppContext): Promise<void> {
    const state = runtime.getState();
    if (!state.valid || !state.claims) return;
    const activeId = state.claims.tenant_id;

    for (const [tenantId, certificate] of await LicenceStatusService.listCertificates(runtime)) {
      const nowSec = Math.floor(Date.now() / 1000);
      const windowDays = state.claims.renewal_window_days ?? 30;
      const windowSec = windowDays * 24 * 3600;
      const exp = tenantId === activeId ? state.claims.exp : decodeExp(certificate);
      if (exp !== null && exp - nowSec > windowSec) continue;

      const urgent = exp !== null && exp - nowSec <= RENEWAL_URGENT_DAYS * 24 * 3600;
      const minInterval = urgent ? RENEWAL_URGENT_INTERVAL_MS : RENEWAL_MIN_INTERVAL_MS;
      const lastRenewal = await SecureLicenceStore.getLastRenewalAt(tenantId);
      if (lastRenewal && Date.now() - new Date(lastRenewal).getTime() < minInterval) continue;

      if (!NetworkStatusService.isBrowserOnline()) {
        await LicenceRenewalService.recordRenewalState(tenantId, 'offline');
        continue;
      }
      const result = await LicenceRenewalService.exchangeCertificateDetailed(
        runtime,
        ctx,
        tenantId,
        certificate,
      );
      await LicenceRenewalService.recordRenewalState(tenantId, result.outcome, result.code);
      if (result.ok) {
        await SecureLicenceStore.setLastRenewalAt(tenantId, new Date().toISOString());
        logger.info({ event: 'licence_renewed', tenantId });
      }
    }
  }

  private static async postRenew(
    endpoint: string,
    body: Record<string, unknown>,
  ): Promise<{
    ok: boolean;
    status: number;
    data: { certificate?: string; server_time?: string };
    unreachable: boolean;
  }> {
    if (window.electron?.activateRemote) {
      try {
        const result = await window.electron.activateRemote(endpoint, body);
        return {
          ok: result.ok,
          status: result.status,
          data: result.data as { certificate?: string; server_time?: string },
          unreachable: false,
        };
      } catch {
        return { ok: false, status: 0, data: {}, unreachable: true };
      }
    }

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(15_000),
      });
      const data = (await res.json().catch(() => ({}))) as {
        certificate?: string;
        server_time?: string;
      };
      return { ok: res.ok, status: res.status, data, unreachable: false };
    } catch {
      return { ok: false, status: 0, data: {}, unreachable: true };
    }
  }

  private static async exchangeCertificate(
    runtime: LicenceRuntime,
    ctx?: AppContext,
    tenantIdArg?: string,
    certificateArg?: string,
  ): Promise<boolean> {
    const result = await LicenceRenewalService.exchangeCertificateDetailed(
      runtime,
      ctx,
      tenantIdArg,
      certificateArg,
    );
    const tenantId = tenantIdArg ?? runtime.getState().claims?.tenant_id;
    if (tenantId) await LicenceRenewalService.recordRenewalState(tenantId, result.outcome, result.code);
    return result.ok;
  }

  private static async exchangeCertificateDetailed(
    runtime: LicenceRuntime,
    ctx?: AppContext,
    tenantIdArg?: string,
    certificateArg?: string,
  ): Promise<{ ok: boolean; outcome: RenewalOutcome; code?: string }> {
    const activeId = runtime.getState().claims?.tenant_id ?? null;
    const tenantId = tenantIdArg ?? activeId;
    const jwt = certificateArg ?? (await SecureLicenceStore.getActivationCertificate());
    const fingerprint = await SecureLicenceStore.getDeviceFingerprint();
    if (!jwt || !fingerprint) return { ok: false, outcome: 'server_error' };
    const isActive = tenantId === null || tenantId === activeId;

    const endpoints = resolveRenewalEndpoints();

    const renewBody = {
      certificate: jwt,
      device_fingerprint: fingerprint,
    };

    let outcome: RenewalOutcome = 'offline';
    let lastCode: string | undefined;
    for (const endpoint of endpoints) {
      try {
        const posted = await LicenceRenewalService.postRenew(endpoint, renewBody);
        if (posted.unreachable) continue;
        if (posted.status === 403) {
          // Révocation / suspension / échéance : lecture seule immédiate (ADR-003), jamais ignoré.
          const code = LicenceStatusService.extractErrorCode(posted.data);
          await LicenceStatusService.handleServerRefusal(code, tenantId ?? undefined);
          outcome = outcomeFromServerCode(code);
          lastCode = code;
          continue;
        }
        if (!posted.ok) {
          outcome = 'server_error';
          continue;
        }

        const data = posted.data;
        if (!data.certificate) {
          outcome = 'server_error';
          continue;
        }

        const verifier = new ActivationCertificateVerifier();
        const serverTimeMs = data.server_time ? Date.parse(data.server_time) : Number.NaN;
        const trustedNowSec = Number.isFinite(serverTimeMs)
          ? Math.floor(serverTimeMs / 1000)
          : Math.floor(Date.now() / 1000);
        const check = await verifier.verifyCertificate(
          data.certificate,
          fingerprint,
          '',
          0,
          trustedNowSec,
        );
        if (!check.valid) {
          outcome = 'server_error';
          continue;
        }
        // Un certificat renouvelé doit rester celui de la MÊME entreprise.
        if (tenantId && check.claims?.tenant_id && check.claims.tenant_id !== tenantId) {
          outcome = 'server_error';
          continue;
        }

        const certTenant =
          check.claims?.tenant_id ?? tenantId ?? (await SecureLicenceStore.getActiveTenantId()) ?? 'unknown';
        if (isActive) {
          await SecureLicenceStore.upsertPortfolioCertificate(certTenant, data.certificate);
        } else {
          // Entreprise non active : on met à jour son certificat sans changer d'entreprise.
          await SecureLicenceStore.setPortfolioCertificate(certTenant, data.certificate);
        }
        if (data.server_time) await ClockTamperGuard.recordTrustedServerTime(data.server_time);
        if (isActive) {
          await runtime.loadFromStore();
          if (ctx) await syncLicenceEntitlements(ctx, runtime);
          notifyLicenceUpdated();
        }
        // Un nouveau certificat à échéance repoussée prouve que la licence est de nouveau valide :
        // on lève sans attendre la restriction « échue » (contrôle de statut forcé, signé par le serveur).
        if (isActive && runtime.getRestriction()) {
          void LicenceStatusService.checkStatus(runtime, { force: true });
        }
        return { ok: true, outcome: 'renewed' };
      } catch {
        // repli endpoint suivant
        outcome = 'offline';
      }
    }
    return { ok: false, outcome, ...(lastCode ? { code: lastCode } : {}) };
  }
}

/** Expiration (secondes) lue du corps d'un JWT : indicative, uniquement pour planifier un renouvellement. */
function decodeExp(jwt: string): number | null {
  try {
    const part = jwt.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}
